import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Should you work at a startup",
  "description": "",
  "published": "2018-10-05T00:00:00.000Z",
  "redirect_from": ["/blog/should-you-work-at-a-startup/swizec/8635"],
  "categories": "Opinions, Thoughts",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><em parentName="p">{`"If you're so good at this, why don't you start your own business?"`}</em></p>
    <p><em parentName="p">{`"Dude, they pay me so much. Like so much. My skills are in such high demand these days. You have no idea. Starting my own business would be the dumbest thing in the world right now.`}</em></p>
    <p>{`Press play, continue reading:`}</p>
    <lite-youtube {...{
      "videoid": "mQPjKSVe1tQ",
      "videostartat": "0"
    }}></lite-youtube>
    <p>{`It was 6 or 7 months ago at some sort of dinner at Red Dog in SoMa. My friend likes to brag when you poke him, but he makes a good point 👇`}</p>
    <p>{`If you can get paid really well, have stable income, work on problems you enjoy solving, `}<em parentName="p">{`and`}</em>{` work in nice offices with free lunch ... why would you resist? Go be a normie.`}</p>
    <p>{`Think about it. A software engineering job in BigTech pays anywhere from $150,000 to unlimited. Some cash, some stock.`}</p>
    <p>{`Another friend of mine sold his BigTech stock a year into his job. Paid for an MBA program in New York cash. No debt, just 3 years in Silicon Valley BigTech. Not even an engineer.`}</p>
    <p>{`3 years, 2 jobs.`}</p>
    <p>{`My engineering friend from Red Dog? I ran into him outside a climbing gym the other day. 3rd job in 6 months.`}</p>
    <p>{`That sounds like a lot, but we seem to run into each other at the boundaries between his jobs.`}</p>
    <p>{`You know how greetings work in Silicon Valley?`}</p>
    <p><em parentName="p">{`"Hey, how are you?"`}</em></p>
    <p><em parentName="p">{`"Good, you?"`}</em></p>
    <p><em parentName="p">{`"Killing it. You still at ... what was it again?"`}</em></p>
    <p>{`Seriously, first thing you ask. Are you still at the same job you had last time we talked? Bonkers.`}</p>
    <h2 {...{
      "id": "the-modern-silicon-valley-hustler-is-an-employee-with-the-mindset-of-a-freelancer"
    }}>{`The modern Silicon Valley hustler is an employee with the mindset of a freelancer`}</h2>
    <p>{`You get a job. You do the thing. You move on.`}</p>
    <p>{`If you like the company and think it's a rocket, you wait out your vesting cliff. Maybe even the whole vesting period.`}</p>
    <p>{`A business coach once told me that vesting is considered finished for retention purposes when an employee hits 75%. That's the point where their vested stock outweighs what's left to vest and they start looking.`}</p>
    <p>{`The way vesting works is that you get 4 years to gain the right to purchase all of your stock for a discounted price. Those are options. If you have RSUs, you buy your stock in advance and the company has options. After 4 years the company loses its options on your stock.`}</p>
    <p>{`Financial mumbo jumbo.`}</p>
    <p>{`Point is, you get 0% in the first year. Then you get 25% of your grant. After that you accrue monthly until you hit 100% four years later.`}</p>
    <p>{`This is how Silicon Valley companies retain their employees.`}</p>
    <p>{`Work at the right company and you get to build your wealth `}<em parentName="p">{`and`}</em>{` get the big cash bucks at the same time. Don't even have to save. But you still should. Stock, especially not-public stock, is a lottery ticket.`}</p>
    <p>{`But you'll notice that recruiter reach-out spikes 1, 3 and 4 years after you start a new job. At year 1 everyone's asking `}<em parentName="p">{`"So are you liking it?"`}</em>{`, at year 3 and 4 everyone's emailing you `}<em parentName="p">{`"Yo! New opportunities, this is fun, come have fun with us! We know you're bored of your job!"`}</em></p>
    <p>{`Uncanny.`}</p>
    <h2 {...{
      "id": "startup-vs-bigtech-vs-start-your-own"
    }}>{`Startup vs. BigTech vs. Start Your Own`}</h2>
    <p>{`You went to an okay school, young with plenty of time, got the fire in your belly for the hustle, really good at what you do. No or few outside commitments.`}</p>
    <p>{`You can start your own business or startup, get a job at a startup, or go work in BigTech.`}</p>
    <p>{`Here we define "business" as a bootstrapped company growing on revenue: delivering value, making cash. No investment.`}</p>
    <p>{`Startup is a VC-backed company at any stage before unicorn.`}</p>
    <p>{`BigTech is a technology company that's unicorn or public.`}</p>
    <p>{`So what will it be?`}</p>
    <h3 {...{
      "id": "bigtech"
    }}>{`BigTech`}</h3>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/10/Photo-Oct-05-3-21-57-PM.jpg",
        "alt": null
      }}></img></p>
    <p>{`BigTech is a job. You won't love it. But you also won't hate it. You'll put up with everybody's bullshit, chant the company line about changing the world while knowing deep down you're just a capitalist machine pumping data for money.`}</p>
    <p>{`Your experience will depend on your boss. Get a great boss, have a great job. Get a bad boss, hate your life.`}</p>
    <p>{`When you climb through the ranks I've heard all sorts of great things can happen. Some even get paid big bucks to do whatever they want and work on open source. That's nice.`}</p>
    <p>{`Your big salary exists to keep you there. BigTech knows you're there for the money. This is a business relationship and there's no bullshit around that.`}</p>
    <p>{`We pay you big big while you're useful. Stop being useful, we drop you like a hot potato.`}</p>
    <p>{`Most common complaints I hear from friends:`}</p>
    <ul>
      <li parentName="ul">{`this is boring, I'm bored`}</li>
      <li parentName="ul">{`I have to fight 5 ranks of managers for a promotion`}</li>
      <li parentName="ul">{`everything takes so long to get done`}</li>
      <li parentName="ul">{`I'm not adding value to society`}</li>
      <li parentName="ul">{`I have so many meetings I have to come early and stay late to get my work done`}</li>
      <li parentName="ul">{`I can't tell how my work contributes`}</li>
      <li parentName="ul">{`too much politics`}</li>
    </ul>
    <p>{`A lot of those come down to bad managers.`}</p>
    <p>{`Say you start with $150k and switch jobs every 18 months for the standard 30% pay bump. Raises are crap because that's 5%. You gotta switch jobs to get a real raise. At the very least switch jobs internally.`}</p>
    <p>{`After 6 years you have: $329k/year compensation.`}</p>
    <p>{`Your wealth and savings depend on which companies' stock you've got, how much you've sold, and how good you've been at avoiding lifestyle inflation. I am confident that at $329k/year you can save at least $150k every year.`}</p>
    <p>{`Yes even living in San Francisco.`}</p>
    <p>{`FIRE (financial independence retire early) in your 40's for sure.`}</p>
    <h3 {...{
      "id": "startup"
    }}>{`Startup`}</h3>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/10/Photo-Oct-05-3-22-10-PM.jpg",
        "alt": null
      }}></img></p>
    <p>{`Startups are a job but not quite. Depends on the size of startup you're in.`}</p>
    <p>{`Small startups just starting out, you're gonna have a lot of fun building cool shit with friends. You have control of what happens, huge impact on company trajectory.`}</p>
    <p>{`Bigger startups morph into BigTech. The bigger the company, the less direct impact you have, the more layers of management, and the less fun you're having.`}</p>
    <p>{`Your compensation is low compared to BigTech, but you get a lottery ticket. This stock of yours, it's going to make it all worth it in the end. You're suffering right now for that huge payout later.`}</p>
    <p>{`In return, you have to trust. Believe. Drink the kool-aid. This startup, it's not just a job, it's your baby! We're in this together! Work long nights, early mornings, long weekends, focus hard all day, put in some muscle.`}</p>
    <p>{`Don't forget we're family. You're doing this for you!`}</p>
    <p>{`Nevermind your ownership percentage is laughable compared to everyone who assures you that this is your baby, your company, and you should work hard. Super hard.`}</p>
    <p>{`Complaints I hear from friends and feel myself:`}</p>
    <ul>
      <li parentName="ul">{`burnout`}</li>
      <li parentName="ul">{`stresss`}</li>
      <li parentName="ul">{`changing company direction every 2 months`}</li>
      <li parentName="ul">{`no idea if what you build today will still be useful tomorrow`}</li>
      <li parentName="ul">{`tired`}</li>
      <li parentName="ul">{`office ran out of paper towels the other day`}</li>
      <li parentName="ul">{`can't plan grown up things because money is tight`}</li>
      <li parentName="ul">{`founder disputes`}</li>
      <li parentName="ul">{`manager disputes`}</li>
      <li parentName="ul">{`will I be able to mention this company on my resume in 5 years?`}</li>
      <li parentName="ul">{`too much politics`}</li>
    </ul>
    <p>{`All the problems you get at a big company except you're not compensated for the bullshit. You're supposed to grin your teeth and love it.`}</p>
    <p>{`Believe the mission!`}</p>
    <p>{`You probably make enough to live your life, save a little, but your wealth and savings are tied to a lottery ticket that might or might not pan out. You never know.`}</p>
    <p>{`6 years from now you could be a millionaire or you could be living on the streets.`}</p>
    <p>{`Ok maybe not the streets. Unless you're dumb enough to work just for equity. But that's on you buddy.`}</p>
    <h3 {...{
      "id": "start-your-own"
    }}>{`Start your own`}</h3>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/10/Photo-Oct-05-3-22-29-PM.jpg",
        "alt": null
      }}></img></p>
    <p>{`Start your own is the most stressful option. Whether you bootstrap or get investment.`}</p>
    <p>{`You take away all the BigTech bullshit and all the Startup bullshit and you're left with all of the control, all of the stress, and none of the compensation.`}</p>
    <p>{`You will suffer, you will work hard, you will stress about money and about whether it's all going to work out.`}</p>
    <p>{`Hire people and `}<em parentName="p">{`you`}</em>{` are responsible for their livelihoods. `}<em parentName="p">{`You`}</em>{` are the person they complain about when they realize you can't pay as much as BigTech. `}<em parentName="p">{`You`}</em>{` are the bad guy when someone complains about drinking mission statement kool-aid instead of getting paid.`}</p>
    <p>{`It sucks. Why would anyone do it?`}</p>
    <p>{`Because it's the only way you can bring `}<em parentName="p">{`your`}</em>{` dream to fruition. `}<em parentName="p">{`You`}</em>{`. `}<em parentName="p">{`Your`}</em>{` company. `}<em parentName="p">{`Your`}</em>{` dream. `}<em parentName="p">{`Your`}</em>{` results. Not somebody else's dream. Not somebody else's job. Your job, your dream, your company.`}</p>
    <p>{`You get to be captain of your own ship, but you also get to be captain of your own ship.`}</p>
    <p>{`Six years from now, maybe you're rich, maybe you're doing okay, maybe you're a beggar on the streets.`}</p>
    <p>{`But what's the worst that can happen? You fail and get a $300k/year job at BigTech from your experience and the network you've built. so cry`}</p>
    <p><img parentName="p" {...{
        "src": "/8330ed3ee578cc23c9e69e3c081de01f/media-94EQmVHkveNck-giphy.gif",
        "alt": null
      }}></img></p>
    <h2 {...{
      "id": "so-what-do-you-do"
    }}>{`So what do you do?`}</h2>
    <p>{`BigTech is stable and pays well, but can be soul crushing. Startup is stressful and pays okay, but can become soul crushing. Founder is stressful and opportunity cost is huge, but can set you up with something you enjoy doing for forever.`}</p>
    <p>{`Lyfe.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      